'use client';

import { getAuthToken, logout, refreshToken } from '@3fourteen/core';
import { useQuery } from '@tanstack/react-query';
import { mainBugsnag } from 'helpers/bugsnag';
import { useMember } from 'hooks/useMember';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { LoadingSkeleton } from 'components';
import { UpgradeSubscription } from 'components/UpgradeSubscription';
import { usePathname, useRouter } from 'next/navigation';
import { TOKEN_REFRESH_INTERVAL_MS } from 'constants/timing';
import { clearAuthCookie, onLogin, setAuthCookie } from 'services/auth';
import { identify, trackLoggedIn } from 'services/mixpanel';
import { routes } from 'services/routes';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const publicRoutes = [routes.login, routes.cookies, routes.forgotPassword];
const allowedPbRoutes = [routes.home, routes.account, routes.research.id];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [path, setPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(getAuthToken());
  const router = useRouter();
  const pathname = usePathname();
  const isProtectedRoute = !publicRoutes.find((route) => route === pathname);

  const refreshInterval = TOKEN_REFRESH_INTERVAL_MS;
  useQuery({
    queryKey: ['refresh-token'],
    queryFn: async () => {
      try {
        const response = await refreshToken();

        setToken(response.authToken);
        setAuthCookie();

        return response.authToken;
      } catch (error: any) {
        const userId = localStorage.getItem('userId');
        const databaseId = localStorage.getItem('databaseId');

        mainBugsnag.notify(error, (event) => {
          event.context = 'background refresh token';
          event.addMetadata('User', {
            userId,
            databaseId,
          });
        });
        clearAuthCookie();
        router.push(routes.login);
        return '';
      }
    },
    refetchInterval: refreshInterval,
    refetchOnWindowFocus: true,
    enabled: isProtectedRoute,
  });

  useEffect(() => {
    if (pathname) {
      setPath(pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      try {
        const { userId } = await onLogin(email, password);

        identify(userId, { email });

        trackLoggedIn(email);

        if (path && !path.includes(routes.login)) {
          router.push(path);
        } else {
          router.push(routes.home);
        }
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        login,
        loading,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export const ProtectRoute = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const pathname = usePathname();
  const isPublicRoute = publicRoutes.includes(pathname);
  const isPbRoute =
    allowedPbRoutes.includes(pathname) ||
    pathname.includes(routes.portfolioBuilder.root);
  const { isAuthenticated, loading } = useAuth();
  const { isPbUser, isCalibanUser, memberships } = useMember();
  const isInactive = memberships && !memberships.length;

  useEffect(() => {
    if (!isPublicRoute && isInactive) {
      router.replace(routes.login);
    }
  }, [isPublicRoute, isInactive, router]);

  useEffect(() => {
    if (!isPublicRoute && isCalibanUser) {
      logout().then(() => {
        localStorage.removeItem('userId');
        localStorage.removeItem('databaseId');
        clearAuthCookie();
        window.location.href = `${routes.login}?error=restricted`;
      });
    }
  }, [isPublicRoute, isCalibanUser]);

  // Public routes bypass all protection
  if (isPublicRoute) {
    return children;
  }

  // Show loading while auth or membership data is resolving,
  // or while a redirect is in progress
  if (loading || !isAuthenticated || !memberships || isInactive || isCalibanUser) {
    return <LoadingSkeleton />;
  }

  if (isPbUser && !isPbRoute) {
    return <UpgradeSubscription />;
  }

  return children;
};
